<script lang="ts">
  /**
   * Reusable two-button confirmation modal — backdrop + centered sheet with
   * a title, optional body text, Cancel + primary action. Caller owns the
   * open state; we only render when open=true and pipe button clicks back
   * through onConfirm / onCancel.
   *
   * Styling intentionally mirrors the existing delete-material modal in
   * AdminMaterialEditPage so the look stays consistent across the admin.
   */
  import { onMount } from 'svelte';

  interface Props {
    open: boolean;
    title: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** When true, the confirm button uses the destructive red palette. */
    danger?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
  }
  let {
    open,
    title,
    message,
    confirmLabel = 'Так, видалити',
    cancelLabel = 'Скасувати',
    danger = true,
    onConfirm,
    onCancel,
  }: Props = $props();

  // Esc to cancel — only when this modal is the active one. We attach on
  // mount and rely on the parent unmounting us when open flips to false.
  onMount(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && open) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
</script>

{#if open}
  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
  <div class="cm-backdrop" aria-hidden="true" onclick={onCancel}></div>
  <div class="cm-sheet" role="alertdialog" aria-modal="true" aria-label={title}>
    <div class="cm-title">{title}</div>
    {#if message}
      <div class="cm-message">{message}</div>
    {/if}
    <div class="cm-actions">
      <button type="button" class="cm-btn cm-btn-secondary" onclick={onCancel}>
        {cancelLabel}
      </button>
      <button
        type="button"
        class="cm-btn"
        class:cm-btn-danger={danger}
        class:cm-btn-primary={!danger}
        onclick={onConfirm}
      >
        {confirmLabel}
      </button>
    </div>
  </div>
{/if}

<style>
  .cm-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9000;
  }
  .cm-sheet {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: calc(100% - 48px);
    max-width: 340px;
    background: var(--bg);
    border-radius: 16px;
    padding: 20px;
    z-index: 9001;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  }
  .cm-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--text);
    margin-bottom: 8px;
    line-height: 1.3;
  }
  .cm-message {
    font-size: 13px;
    color: var(--text-muted);
    line-height: 1.5;
    margin-bottom: 18px;
  }
  .cm-actions {
    display: flex;
    gap: 8px;
  }
  .cm-btn {
    flex: 1;
    padding: 11px;
    border: none;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: filter 0.15s, background 0.15s;
  }
  .cm-btn-secondary {
    background: var(--bg2);
    color: var(--text);
    border: 1px solid var(--border);
  }
  .cm-btn-secondary:hover {
    background: var(--border);
  }
  .cm-btn-danger {
    background: var(--heart);
    color: var(--text-light);
  }
  .cm-btn-danger:hover {
    filter: brightness(1.05);
  }
  .cm-btn-primary {
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
  }
  .cm-btn-primary:hover {
    filter: brightness(1.05);
  }
</style>
